import React, { useState, useRef, useEffect, useCallback, useReducer } from 'react';
import axios, { AxiosInstance } from 'axios';
import { useAuth } from '../auth-provider';
import { ApiResponse } from '../../types/api.types';
import { 
  Product, Stage, Context, Attribute,
  isProduct, isStage, isContext, isAttribute 
} from '../../types/domain.types';
import { DataState, DataAction, DsmValues, ProductCache, Props, DraftChangeRow } from './dsm.types';

const DsmContext = React.createContext<undefined | DsmValues>(undefined);
const Provider = DsmContext.Provider;

// Generic reducer for data fetching
function dataReducer<T>(state: DataState<T>, action: DataAction<T>): DataState<T> {
  switch (action.type) {
    case 'FETCH_START':
      return { status: 'loading' };
    case 'FETCH_SUCCESS':
      return { status: 'success', data: action.payload };
    case 'FETCH_ERROR':
      return { status: 'error', error: action.error };
    default:
      return state;
  }
}

function DsmProvider({ children }: Props) {
  
  // Auth context
  const { user } = useAuth();
  
  // Axios setup
  const axiosInstance = useRef<AxiosInstance | null>(null);
  const abortController = useRef<AbortController>(new AbortController());
  
  // Product state using reducer
  const [productsState, dispatchProducts] = useReducer(
    dataReducer<Product[]>, 
    { status: 'idle' }
  );
  
  // Stage state - both full and filtered collections
  const [stagesState, dispatchStages] = useReducer(
    dataReducer<Stage[]>,
    { status: 'idle' }
  );
  
  // Context state - both full and filtered collections
  const [contextsState, dispatchContexts] = useReducer(
    dataReducer<Context[]>,
    { status: 'idle' }
  ); 
  
  // Attribute state - both full and filtered collections
  const [attributesState, dispatchAttributes] = useReducer(
    dataReducer<Attribute[]>,
    { status: 'idle' }
  );

  // Add isReleaseMode state to manage release/draft mode
  const [isReleaseMode, setIsReleaseMode] = useState<boolean>(true);  

  // Add isAdminUiVisible to hide CRUD operations on product, stages, contexts (only allow attribute editing for now)
  const [isAdminUiVisible, setIsAdminUiVisible] = useState<boolean>(false);  

  // Filtered collections
  const [filteredStages, setFilteredStages] = useState<Stage[]>([]);
  const [filteredContexts, setFilteredContexts] = useState<Context[]>([]);
  const [filteredAttributes, setFilteredAttributes] = useState<Attribute[]>([]);
  
  // Selected entity states
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [selectedContext, setSelectedContext] = useState<Context | null>(null);
  
  // Cache states for stages, contexts, and attributes
  const [stagesCache, setStagesCache] = useState<ProductCache<Stage>>({});
  const [contextsCache, setContextsCache] = useState<ProductCache<Context>>({});
  const [attributesCache, setAttributesCache] = useState<ProductCache<Attribute>>({});

  // Draft changes state
  const [draftChangesState, setDraftChangesState] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    data?: DraftChangeRow[];
    error?: string;
  }>({ status: 'idle' });

  useEffect(() => {

    clearCaches();
    loadProducts();

    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!user) return;    
    axiosInstance.current = null;
    initAxiosInstance();
  }, [user]);

  useEffect(() => {
    console.log(`DsmProvider: Selected product changed to ${selectedProduct?.product || 'none'}`);

    // Reset stage and context selections
    setSelectedStage(null);
    setSelectedContext(null);
    
    // If a product is selected, load its data
    if (selectedProduct) {
      const loadProductData = async () => {
        try {
          // Wait for stages to load first
          const stagesLoaded = await loadStagesForProduct(selectedProduct.product);
          
          // Then load contexts
          const contextsLoaded = await loadContextsForProduct(selectedProduct.product);
          
          // Finally load attributes
          const attributesLoaded = await loadAttributesForProduct(selectedProduct.product);
          
          console.log(`Data loading complete - Stages: ${stagesLoaded}, Contexts: ${contextsLoaded}, Attributes: ${attributesLoaded}`);
        } catch (error) {
          console.error("Error loading product data:", error);
        }
      };
      
      // Call the async function
      loadProductData();
    } else {
      // No product selected, clear filtered collections
      setFilteredStages([]);
      setFilteredContexts([]);
      setFilteredAttributes([]);
    }
  }, [selectedProduct]);

  useEffect(() => {

    setSelectedContext(null);

    if (selectedProduct) {
      // If stage is null (None selected), show all contexts for the product
      if (!selectedStage) {
        if (contextsCache[selectedProduct.product]) {
          const filteredByMode = contextsCache[selectedProduct.product].filter(
            (context) => context.context.endsWith(isReleaseMode ? '/release' : '/draft')
          );
          setFilteredContexts(filteredByMode);
        }
      } 
      // Otherwise filter contexts by the selected stage
      else {
        const stageContexts = contextsCache[selectedProduct.product]?.filter(
          (context) =>
            context.stage === selectedStage.stage &&
            context.context.endsWith(isReleaseMode ? '/release' : '/draft')
        ) || [];
        setFilteredContexts(stageContexts);
      }

      // Filter attributes based on selected product and stage
      filterAttributes();
    } else {
      setFilteredContexts([]);
      setFilteredAttributes([]);
    }
  }, [selectedStage, contextsCache, isReleaseMode]);

  useEffect(() => {
    filterAttributes();
  }, [selectedContext, isReleaseMode]);  

  useEffect(() => {
    if (!selectedProduct) {
      setFilteredAttributes([]);
      return;
    }

    let filtered = attributesCache[selectedProduct.product] || [];

    // Filter by stage if selected
    if (selectedStage) {
      filtered = filtered.filter(attr => attr.stage === selectedStage.stage);
    }

    // Filter by context if selected
    if (selectedContext) {
      filtered = filtered.filter(attr => attr.context === selectedContext.context);
    }

    // Filter by release or draft mode
    const suffixToMatch = isReleaseMode ? '/release' : '/draft';
    filtered = filtered.filter(attr => attr.context.endsWith(suffixToMatch));

    setFilteredAttributes(filtered);
  }, [selectedProduct, selectedStage, selectedContext, isReleaseMode, attributesCache]);

  const filterAttributes = () => {
    if (!selectedProduct) {
      setFilteredAttributes([]);
      return;
    }

    let filtered = attributesCache[selectedProduct.product] || [];

    // If stage is selected, filter by stage
    if (selectedStage) {
      filtered = filtered.filter(
        (attr) => !attr.stage || attr.stage === selectedStage.stage
      );
    }

    // If context is selected, filter by context
    if (selectedContext) {
      filtered = filtered.filter(
        (attr) =>
          (!attr.context || attr.context === selectedContext.context) &&
          attr.context.endsWith(isReleaseMode ? '/release' : '/draft')
      );
    }

    setFilteredAttributes(filtered);
  };  

  const initAxiosInstance = useCallback(() => {
    if (!user || axiosInstance.current) return;
    
    // console.log(`DsmProvider: Initializing axios instance`);
    axiosInstance.current = axios.create({
      baseURL: process.env.REACT_APP_DSM_API_URL!,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.idToken
      },
      withCredentials: true 
    });
  }, [user]);

  const loadProducts = async (resource = 'products'): Promise<boolean> => {
    if (!user) return false;
    
    // Ensure axios is initialized
    initAxiosInstance();
    
    try {
      // Update state to loading
      dispatchProducts({ type: 'FETCH_START' });
      
      // Make API call
      const response = await axiosInstance.current!.get<ApiResponse<Product>>(
        `/${resource}`,
        { signal: abortController.current.signal }
      );
      
      // Process and validate response data
      const items = response.data.items;
      
      // Type guard to ensure we only have valid products
      const validProducts = items
        .filter(item => isProduct(item))
        .map(item => item as Product);
      
      // Update state with products
      dispatchProducts({ 
        type: 'FETCH_SUCCESS', 
        payload: validProducts 
      });
      
      console.log(`DsmProvider: Loaded ${validProducts.length} products`);
      return true;
      
    } catch (err: any) {
      // Handle errors
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load products';
      dispatchProducts({ 
        type: 'FETCH_ERROR', 
        error: errorMessage 
      });
      
      console.error(`DsmProvider: Error loading products:`, err);
      return false;
    }
  };

  const loadStagesForProduct = async (productId: string, forceRefresh = false): Promise<boolean> => {
    
    if (!user) return false;
    
    // Skip cache if forceRefresh is true
    if (!forceRefresh && stagesCache[productId]) {
      console.log(`DsmProvider: Using cached stages for product ${productId}`);
      setFilteredStages(stagesCache[productId]);
      dispatchStages({ type: 'FETCH_SUCCESS', payload: stagesCache[productId] });
      return true;
    }
    
    // Ensure axios is initialized
    initAxiosInstance();
    
    try {
      // Update state to loading
      dispatchStages({ type: 'FETCH_START' });
      
      // Make API call
      const response = await axiosInstance.current!.get<ApiResponse<Stage>>(
        `/products/${productId}/stages`,
        { signal: abortController.current.signal }
      );
      
      // Process and validate response data
      const items = response.data.items;
      
      // Type guard to ensure we only have valid stages
      const validStages = items
        .filter(item => isStage(item) && item.product === productId)
        .map(item => item as Stage);
      
      // Update cache (only cache non-empty results)
      // if (validStages.length > 0) {
      //   setStagesCache(prev => ({
      //     ...prev,
      //     [productId]: validStages
      //   }));
      // }
      setStagesCache(prev => ({
        ...prev,
        [productId]: validStages
      }));
      
      // Update filtered stages for the current product
      setFilteredStages(validStages);
      
      // Update state with stages
      dispatchStages({ 
        type: 'FETCH_SUCCESS', 
        payload: validStages 
      });
      
      console.log(`DsmProvider :: Loaded ${validStages.length} stages for product ${productId}`);

      return true;
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load stages';
      dispatchStages({ 
        type: 'FETCH_ERROR', 
        error: errorMessage 
      });
      
      console.error(`DsmProvider :: Error loading stages:`, err);
      return false;
    }
  };

  const loadContextsForProduct = async (productId: string, forceRefresh = false): Promise<boolean> => {
    
    if (!user) return false;  

    // Skip cache if forceRefresh is true
    if (!forceRefresh && contextsCache[productId]) {
      console.log(`DsmProvider: Using cached contexts for product ${productId}`);
      
      // Apply stage filter even when using cached data
      if (selectedStage) {
        const stageContexts = contextsCache[productId].filter(ctx => ctx.stage === selectedStage.stage);
        setFilteredContexts(stageContexts);
      } else {
        setFilteredContexts(contextsCache[productId]);
      }
      
      dispatchContexts({ type: 'FETCH_SUCCESS', payload: contextsCache[productId] });
      return true;
    }

    // Ensure axios is initialized
    initAxiosInstance();
    
    try {
      // Update state to loading
      dispatchContexts({ type: 'FETCH_START' });
      
      // Make API call - fetch all contexts for this product
      const response = await axiosInstance.current!.get<ApiResponse<Context>>(
        `/products/${productId}/contexts`,
        { signal: abortController.current.signal }
      );
      
      // Process and validate response data
      const items = response.data.items;
      
      // Type guard to ensure we only have valid contexts
      const validContexts = items
        .filter(item => isContext(item) && item.product === productId)
        .map(item => item as Context);
      
      // Update cache (only cache non-empty results)
      // if (validContexts.length > 0) {
      //   setContextsCache(prev => ({
      //     ...prev,
      //     [productId]: validContexts
      //   }));
      // }
      setContextsCache(prev => ({
        ...prev,
        [productId]: validContexts
      }));
      
      // Update filtered contexts
      if (selectedStage) {
        // Filter by stage if one is selected
        const stageContexts = validContexts.filter(ctx => ctx.stage === selectedStage.stage);
        setFilteredContexts(stageContexts);
      } else {
        // Otherwise show all contexts for this product
        setFilteredContexts(validContexts);
      }
      
      // Update state with all contexts for this product
      dispatchContexts({ 
        type: 'FETCH_SUCCESS', 
        payload: validContexts 
      });
      
      console.log(`DsmProvider: Loaded ${validContexts.length} contexts for product ${productId}`);
      return true;
      
    } catch (err: any) {
      // Handle errors
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load contexts';
      dispatchContexts({ 
        type: 'FETCH_ERROR', 
        error: errorMessage 
      });
      
      console.error(`DsmProvider: Error loading contexts:`, err);
      return false;
    }
  };

  const loadAttributesForProduct = async (productId: string, forceRefresh = false): Promise<boolean> => {
      
    if (!user) return false;
    
    // Skip cache if forceRefresh is true
    if (!forceRefresh && attributesCache[productId]) {
      console.log(`DsmProvider: Using cached attributes for product ${productId}`);
      // Filtered attributes will be set based on stage and context selections
      filterAttributes();
      dispatchAttributes({ type: 'FETCH_SUCCESS', payload: attributesCache[productId] });
      return true;
    }

    // Ensure axios is initialized
    initAxiosInstance();
    
    try {
      // Update state to loading
      dispatchAttributes({ type: 'FETCH_START' });
      
      // Make API call - fetch all attributes for this product
      const response = await axiosInstance.current!.get<ApiResponse<Attribute>>(
        `/products/${productId}/attributes`,
        { signal: abortController.current.signal }
      );
      
      // Process and validate response data
      const items = response.data.items;
      
      // Type guard to ensure we only have valid attributes
      const validAttributes = items
        .filter(item => isAttribute(item) && item.product === productId)
        .map(item => item as Attribute);
      
      // Always update cache with validAttributes (even if empty)
      setAttributesCache(prev => ({
        ...prev,
        [productId]: validAttributes
      }));
      
      // Directly set filtered attributes based on current selection state
      if (selectedStage || selectedContext) {
        // If selections exist, use the filter function
        filterAttributes();
      } else {
        // If no stage/context selected, show all attributes for this product
        setFilteredAttributes(validAttributes);
      }
      
      // Update state with all attributes for this product
      dispatchAttributes({ 
        type: 'FETCH_SUCCESS', 
        payload: validAttributes 
      });
      
      console.log(`DsmProvider: Loaded ${validAttributes.length} attributes for product ${productId}`);
      return true;
      
    } catch (err: any) {
      // Handle errors
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load attributes';
      dispatchAttributes({ 
        type: 'FETCH_ERROR', 
        error: errorMessage 
      });
      
      console.error(`DsmProvider: Error loading attributes:`, err);
      return false;
    }
  };  

  const flattenDraftChanges = (apiData: any): DraftChangeRow[] => {
    const rows: DraftChangeRow[] = [];
    if (!apiData) return rows;
    Object.entries(apiData).forEach(([stage, contexts]) => {
      Object.entries(contexts as any).forEach(([context, ctxObj]: [string, any]) => {
        if (ctxObj.changes && Array.isArray(ctxObj.changes)) {
          ctxObj.changes.forEach((change: any) => {
            rows.push({
              stage,
              context,
              att_name: change.att_name,
              action: change.action,
              version: change.version,
              user: change.user,
              ts: formatDraftChangeTimestamp(change.ts),
              change: getChangedKeys(change.before, change.after),
              before: change.before,
              after: change.after,
            });
          });
        }
      });
    });
    return rows;
  };

  const loadDraftChanges = useCallback(async (productId: string) => {
    setDraftChangesState({ status: 'loading' });
    try {
      if (!user || !axiosInstance.current) throw new Error('Not authenticated');
      const response = await axiosInstance.current.get(`/products/${productId}/draft-changes`);
      const rows = flattenDraftChanges(response.data);
      setDraftChangesState({ status: 'success', data: rows });
      return true;
    } catch (err: any) {
      setDraftChangesState({ status: 'error', error: err.message || 'Failed to load draft changes' });
      return false;
    }
  }, [user, axiosInstance, flattenDraftChanges]);

  const handleProductSelection = (product: Product | null) => {
    setSelectedProduct(product);
  };

  const handleStageSelection = (stage: Stage | null) => {
    setSelectedStage(stage);
  };

  const clearCaches = () => {
    setStagesCache({});
    setContextsCache({});
    setAttributesCache({});
  };

  const createEntity = async <T extends { product: string }>(
    entityType: 'product' | 'stage' | 'context' | 'attribute',
    data: T,
    productId?: string
  ): Promise<boolean> => {
    
    if (!user || !axiosInstance.current) return false;
    
    try {

      let endpoint = '';
      let payload: any = data;

      console.log(`DsmProvider.createEntity :: entityType: ${entityType}, data: ${JSON.stringify(data)}`);
      
      switch (entityType) {
        case 'product':
          endpoint = '/products';
          break;
        case 'stage':
          endpoint = `/products/${data.product}/stages`;
          break;
        case 'context': {
          const contextData = data as unknown as Context;
          if (!contextData.stage) {
            console.error('Context data missing required stage property');
            return false;
          }
          endpoint = `/products/${contextData.product}/stages/${contextData.stage}/contexts`;
          break;
        }
        case 'attribute': {

          const attr = data as unknown as { product: string; stage: string; context: string; att_name: string; att_type: string; dq_rule_set?: any; user_name?: string };
          if (!attr.product || !attr.stage || !attr.context) {
            console.error('Attribute data missing required product, stage, or context property');
            return false;
          }
          endpoint = `/products/${attr.product}/stages/${attr.stage}/contexts/${encodeURIComponent(attr.context)}/attributes`;
          payload = {
            att_name: attr.att_name,
            att_type: attr.att_type,
            dq_rule_set: attr.dq_rule_set || [],
            user_name: attr.user_name || user.email || 'api',
          };

          break;
        }
      }

      const response = await axiosInstance.current.post(endpoint, payload);

      console.log(`DsmProvider.createEntity :: response: ${JSON.stringify(response)}`);

      // Refresh the corresponding data collection
      switch (entityType) {
        case 'product':
          await loadProducts(undefined);
          break;
        case 'stage':
          await loadStagesForProduct(data.product, true);
          break;
        case 'context':
          await loadContextsForProduct(data.product, true);
          break;
        case 'attribute':
          await loadAttributesForProduct(data.product, true);
          break;
      }
      
      return true;

    } catch (err: any) {
      console.error(`Error creating ${entityType}:`, err);
      return false;
    }
  };  

  const updateEntity = async <T extends { product: string; stage?: string; context?: string }>(
    entityType: 'product' | 'stage' | 'context' | 'attribute',
    data: T,
    id: string): Promise<boolean> => {

    if (!user || !axiosInstance.current) return false;
    
    try {

      let endpoint = '';
      const context_encoded = encodeURIComponent(data.context || '');
      
      switch (entityType) {
        case 'product':
          endpoint = `/products/${id}`;
          break;
        case 'stage':
          endpoint = `/products/${data.product}/stages/${id}`;
          break;
        case 'context':
          if (!data.stage) throw new Error('Stage is required for context updates.');
          endpoint = `/products/${data.product}/stages/${data.stage}/contexts/${id}`;
          break;
        case 'attribute':
          if (!data.stage || !data.context) throw new Error('Stage and Context are required for attribute updates.');
          endpoint = `/products/${data.product}/stages/${data.stage}/contexts/${context_encoded}/attributes/${id}`;
          break;
      }
      
      console.log(`DsmProvider.updateEntity :: entityType: ${entityType}, id: ${id}, data: ${JSON.stringify(data)}`);
      // const response = await axiosInstance.current.put(endpoint, data);
      
      // Exclude att_name for attributes
      const payload = entityType === 'attribute' ? { ...data, att_name: undefined } : data;

      const response = await axiosInstance.current.put(endpoint, payload);
      console.log(`DsmProvider.updateEntity :: response: ${JSON.stringify(response.data)}`);

      // Refresh the corresponding data collection
      switch (entityType) {
        case 'product':
          await loadProducts(undefined);
          break;
        case 'stage':
          await loadStagesForProduct(data.product, true);
          break;
        case 'context':
          await loadContextsForProduct(data.product, true);
          break;
        case 'attribute':
          await loadAttributesForProduct(data.product, true);
          break;
      }
      
      return true;
    } catch (err: any) {
      console.error(`Error updating ${entityType}:`, err);
      return false;
    }
  };

  const deleteEntity = async (
    entityType: 'product' | 'stage' | 'context' | 'attribute',
    productId: string,
    id: string,
    stageId?: string,
    contextId?: string
  ): Promise<boolean> => {
    if (!user || !axiosInstance.current) return false;
    try {
      let endpoint = '';
      switch (entityType) {
        case 'product':
          endpoint = `/products/${id}`;
          break;
        case 'stage':
          endpoint = `/products/${productId}/stages/${id}`;
          break;
        case 'context': {
          // For context, we need the stage
          if (!stageId) {
            // Try to find context in cache to get its stage
            const contextItem = contextsCache[productId]?.find(c => c.context === id);
            if (!contextItem || !contextItem.stage) {
              console.error('Context delete failed: Missing stage ID');
              return false;
            }
            stageId = contextItem.stage;
          }
          endpoint = `/products/${productId}/stages/${stageId}/contexts/${id}`;
          break;
        }
        case 'attribute': {
          // For attribute, need productId, stageId, contextId, attributeId
          if (!stageId || !contextId) {
            console.error('Attribute delete failed: Missing stageId or contextId');
            return false;
          }
          endpoint = `/products/${productId}/stages/${stageId}/contexts/${encodeURIComponent(contextId)}/attributes/${id}`;
          break;
        }
      }
      const response = await axiosInstance.current.delete(endpoint);
      // Refresh the corresponding data collection
      switch (entityType) {
        case 'product':
          await loadProducts(undefined);
          break;
        case 'stage':
          await loadStagesForProduct(productId, true);
          break;
        case 'context':
          await loadContextsForProduct(productId, true);
          break;
        case 'attribute':
          await loadAttributesForProduct(productId, true);
          break;
      }
      return true;
    } catch (err: any) {
      console.error(`Error deleting ${entityType}:`, err);
      return false;
    }
  };

  const createProduct = async (data: Partial<Product>): Promise<boolean> => {
    return createEntity('product', data as any);
  };

  const updateProduct = async (data: Partial<Product>, id: string): Promise<boolean> => {
    return updateEntity('product', data as any, id);
  };

  const deleteProduct = async (id: string): Promise<boolean> => {
    if (selectedProduct?.product === id) {
      setSelectedProduct(null);
    }
    return deleteEntity('product', id, id);
  };

  const createStage = async (data: Partial<Stage>): Promise<boolean> => {
    if (!data.product) return false;
    return createEntity('stage', data as any);
  };

  const updateStage = async (data: Partial<Stage>, id: string): Promise<boolean> => {    
    if (!data.product) return false;
    if (selectedStage?.stage === id) {
      setSelectedStage({
        ...selectedStage,
        ...data
      } as Stage);
    }
    
    return updateEntity('stage', data as any, id);
  };

  const deleteStage = async (productId: string, stageId: string): Promise<boolean> => {
    if (selectedStage?.stage === stageId) {
      setSelectedStage(null);
    }
    
    return deleteEntity('stage', productId, stageId);
  };

  const createContext = async (data: Partial<Context>): Promise<boolean> => {
    if (!data.product || !data.stage) return false;
    return createEntity('context', data as any);
  };

  const updateContext = async (data: Partial<Context>, id: string): Promise<boolean> => {
    if (!data.product || !data.stage) return false;
    if (selectedContext?.context === id) {
      setSelectedContext({
        ...selectedContext,
        ...data
      } as Context);
    }
    
    return updateEntity('context', data as any, id);
  };

  const deleteContext = async (productId: string, contextId: string): Promise<boolean> => {
    if (selectedContext?.context === contextId) {
      setSelectedContext(null);
    }
    
    return deleteEntity('context', productId, contextId);
  };

  const createAttribute = async (data: Partial<Attribute>): Promise<boolean> => {
    // Require product, stage, context, att_name, att_type
    if (!data.product || !data.stage || !data.context || !data.att_name || !data.att_type) return false;

    // Compose payload as expected by backend
    const payload = {
      ...data,
      dq_rule_set: data.dq_rule_set || [],
      user_name: user?.userId || 'api',
    };

    // Use generic createEntity, but pass the correct payload
    return createEntity('attribute', payload as any);
  };

  const updateAttribute = async (data: Partial<Attribute>, id: string): Promise<boolean> => {
    if (!data.product) return false;
    return updateEntity('attribute', data as any, id);
  };

  const deleteAttribute = async (productId: string, stageId: string, contextId: string, attributeId: string): Promise<boolean> => {
    return deleteEntity('attribute', productId, attributeId, stageId, contextId);
  };

  const getChangedKeys = (before: any, after: any): string[] => {
    if (!before || !after) return [];
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    return Array.from(keys).filter(
      (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key])
    );
  };

  const formatDraftChangeTimestamp = (ts: string): string => {
    // Example: "20250911223050664" → "2025-09-11 22:30:50"
    if (!ts || ts.length < 14) return ts;
    return `${ts.slice(0,4)}-${ts.slice(4,6)}-${ts.slice(6,8)} ${ts.slice(8,10)}:${ts.slice(10,12)}:${ts.slice(12,14)}`;
  };

  return (
    <Provider value={{

      // Products
      productsState,
      loadProducts,
      selectedProduct,
      setSelectedProduct: handleProductSelection,
      createProduct,
      updateProduct,
      deleteProduct,      
      
      // Stages
      stagesState,
      loadStages: loadStagesForProduct,
      selectedStage,
      setSelectedStage: handleStageSelection,
      filteredStages,
      createStage,
      updateStage, 
      deleteStage,
      
      // Contexts
      contextsState,
      loadContexts: loadContextsForProduct,
      selectedContext,
      setSelectedContext,
      filteredContexts,
      createContext,
      updateContext,
      deleteContext,      
      
      // Attributes
      attributesState,
      loadAttributes: loadAttributesForProduct,
      filteredAttributes,
      createAttribute,
      updateAttribute,
      deleteAttribute,      
      
      // Caching
      clearCaches,

      // Mode
      isReleaseMode,
      setIsReleaseMode,

      isAdminUiVisible,
      setIsAdminUiVisible,

      // Draft Changes
      draftChangesState,
      loadDraftChanges,
    }}>
      {children}
    </Provider>
  );
}

// Hook for consuming the context
const useDsmContext = () => {
  const context = React.useContext(DsmContext);
  if (context === undefined) {
    throw new Error('useDsmContext must be used within a DsmProvider');
  }
  return context;
};

export { DsmProvider, useDsmContext };









